import { Component, Input } from '@angular/core';
import { ContestEvent, ContestEventType, QUESTION_INDEX_COMPARATOR } from '../../../model/contest/ContestEvent';
import { ContestTaskStats, ContestTaskType } from '../../../model/contest/ContestTask';


@Component({
  selector: 'answer-sheet',
  templateUrl: './answer-sheet.component.html',
  styleUrls: ['./answer-sheet.component.scss']
})
export class AnswerSheetComponent {

  private readonly ALPHABET = $localize`:General|Alphabet indexes@@general-alphabet-indexes:ABCDEFGHIJKLMNOPQRSTUVWXYZ`;

  @Input() sectionIndex!: number;

  @Input() set taskStats(stats: ContestTaskStats | undefined) {
    if (stats && this.sectionIndex) {
      this.columnsToDisplay.push('question_index');

      const questionInSection: Record<number, number> = stats.answers_count[this.sectionIndex];
      const maxAnswers = Object.keys(questionInSection)
        .map(questionIndex => questionInSection[parseInt(questionIndex)])
        .reduce((a1, a2) => Math.max(a1, a2));

      for (let i = 1; i <= maxAnswers; i++) {
        this.answerIndexes.push(i);
        this.columnsToDisplay.push(`answer_${i}`);
      }
      this.columnsToDisplay.push('points');

      for (let i = 1; i <= stats.questions_count[this.sectionIndex]; i++) {
        this.questionIndexes.push(i);
        this.answersCount.set(i, stats.answers_count[this.sectionIndex][i] || 0);
      }

      let prevCount = 0;
      for (let i = 1; i < (this.sectionIndex || 0); i++) {
        prevCount += stats.questions_count[i] || 0;
      }
      this.prevSectionsQuestionsCount = prevCount;
    }
  }

  @Input() set taskAnswerEvents(events: ContestEvent[] | undefined) {
    if (events) {
      const answers = events;

      if (this.questionIndexes) {
        const answeredQuestions = new Set<number>();
        answers.forEach(answer => answeredQuestions.add(answer.questionIndex || 0));
        this.questionIndexes
          .filter(questionIndex => !answeredQuestions.has(questionIndex))
          .forEach(questionIndex => {
            const event = new ContestEvent('', ContestTaskType.standard, ContestEventType.answer);
            event.questionIndex = questionIndex;
            event.points = 0;
            answers.push(event);

            this.missingQuestionIndexes.push(questionIndex);
          });
      }

      answers.sort(QUESTION_INDEX_COMPARATOR);

      this.answers = answers;
    }
  }

  prevSectionsQuestionsCount = 0;
  questionIndexes: number[] = [];
  missingQuestionIndexes: number[] = [];
  answerIndexes: number[] = [];
  answers?: ContestEvent[];
  answersCount = new Map<number, number>();

  columnsToDisplay: string[] = [];

  constructor() {
  }

  get questionMinNumber() {
    return this.sectionIndex ? this.prevSectionsQuestionsCount + 1 : 1;
  }

  get questionMaxNumber() {
    return this.questionMinNumber + (this.answers && this.answers.length ? this.answers.length - 1 : 0);
  }

  getQuestionNumber(index: number) {
    return this.prevSectionsQuestionsCount + index;
  }

  get sectionSubtotal() {
    return this.answers
      ? this.answers.map(answer => answer.points || 0).reduce((a1, a2) => a1 + a2)
      : 0;
  }

  getAnswerLabel(questionIndex: number, answerIndex: number): string {
    return answerIndex > 0 && answerIndex <= this.ALPHABET.length
      ? (this.answersCount.get(questionIndex) || 0) >= answerIndex
        ? this.ALPHABET[answerIndex - 1]
        : ''
      : '?';
  }

  private isCorrectEvent(event: ContestEvent): boolean {
    return !!event.answerIndexes
      && !!event.correctAnswerIndexes
      && event.answerIndexes.length == event.correctAnswerIndexes.length
      && event.answerIndexes
        .map(answer => event.correctAnswerIndexes?.includes(answer) || false)
        .reduce((a1, a2) => a1 && a2);
  }

  isQuestionAnswer(event: ContestEvent, answerIndex: number): boolean {
    return (!!event.answerIndexes && event.answerIndexes.includes(answerIndex))
      || (!!event.correctAnswerIndexes && event.correctAnswerIndexes.includes(answerIndex));
  }

  isUserAnswerCorrect(event: ContestEvent, answerIndex: number): boolean {
    return !!event.answerIndexes
      && event.answerIndexes.includes(answerIndex)
      && !!event.correctAnswerIndexes
      && event.correctAnswerIndexes.includes(answerIndex);
  }

  isAnswerIncorrect(event: ContestEvent, answerIndex: number) {
    return !!event.answerIndexes
      && event.answerIndexes.includes(answerIndex)
      && (!event.correctAnswerIndexes || !event.correctAnswerIndexes.includes(answerIndex));
  }

  isAnswerMissing(event: ContestEvent, answerIndex: number): boolean {
    return !!event.correctAnswerIndexes
      && event.correctAnswerIndexes.includes(answerIndex)
      && (!event.answerIndexes || !event.answerIndexes.includes(answerIndex))
      && answerIndex <= (this.answersCount.get(event.questionIndex as number) || 0);
  }
}
